import React, { useState } from 'react';
import { Save, Send, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { waApi, errMsg } from './waApi';

const Chip = ({ on, label, onClick, tone = 'emerald', testId, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
      disabled ? 'opacity-40 cursor-not-allowed border-zinc-800 text-zinc-500' :
      on ? (tone === 'rose' ? 'bg-rose-500/15 border-rose-500/50 text-rose-200' : 'bg-emerald-500/15 border-emerald-500/50 text-emerald-200')
         : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:text-zinc-300'}`}>
    {label}
  </button>
);

export const WaAccessRow = ({ user, catalog, onSaved }) => {
  const [wa, setWa] = useState(user.whatsapp);
  const [saving, setSaving] = useState(false);
  const toggle = (group, key) => setWa(w => ({ ...w, [group]: { ...w[group], [key]: !w[group][key] } }));
  const moneyLocked = user.can_see_prices === false && !['owner', 'admin'].includes(user.role);

  const save = async () => {
    setSaving(true);
    try {
      const res = await waApi.saveAccess(user.id, { number: wa.number, enabled: wa.enabled, scopes: wa.scopes, notify: wa.notify });
      setWa(res.whatsapp); onSaved?.(res); toast.success(`Saved WhatsApp access for ${user.name}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const sendTest = async () => {
    try {
      const r = await waApi.sendTest(user.id);
      const msg = r.simulated ? 'Simulator mode — no provider configured, message logged only'
        : !r.ok ? `Send failed: ${r.error}`
        : r.mode === 'template' ? 'Welcome template sent' : r.note ? `Sent, but: ${r.note}` : 'Test message sent';
      toast[r.simulated ? 'info' : r.ok ? (r.note ? 'warning' : 'success') : 'error'](msg);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 space-y-3" data-testid={`wa-access-row-${user.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[160px]">
          <div className="text-zinc-100 font-semibold text-sm">{user.name}</div>
          <div className="text-[11px] text-zinc-500">{user.role} · {user.email || '—'}</div>
          {wa.number && (
            <div className={`text-[10px] mt-0.5 ${user.window_open ? 'text-emerald-400' : 'text-zinc-500'}`} data-testid={`wa-window-${user.id}`}>
              {user.window_open ? '● chatting (24h window open)' : user.last_inbound_at ? `○ last message ${new Date(user.last_inbound_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}` : '○ never messaged TI'}
            </div>
          )}
        </div>
        <input value={wa.number || ''} onChange={e => setWa({ ...wa, number: e.target.value })} placeholder="WhatsApp number e.g. 9198XXXXXXXX"
          data-testid={`wa-number-input-${user.id}`}
          className="bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-1.5 text-sm text-zinc-100 w-56 focus:border-emerald-500/60 outline-none" />
        <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
          <input type="checkbox" checked={!!wa.enabled} onChange={e => setWa({ ...wa, enabled: e.target.checked })}
            data-testid={`wa-enabled-toggle-${user.id}`} className="accent-emerald-500 w-4 h-4" />
          Bot enabled
        </label>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={sendTest} disabled={!wa.number} data-testid={`wa-send-test-${user.id}`}
            className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-xl border border-zinc-700 text-zinc-300 hover:border-emerald-500/50 disabled:opacity-40">
            <Send size={12} /> Test
          </button>
          <button onClick={save} disabled={saving} data-testid={`wa-save-${user.id}`}
            className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-xl bg-[#25D366] text-black font-semibold hover:bg-[#1ebe5a] disabled:opacity-50">
            <Save size={12} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Can ask about</div>
          <div className="flex flex-wrap gap-1.5">
            {catalog.read_scopes.map(s => (
              <Chip key={s.key} on={!!wa.scopes[s.key]} onClick={() => toggle('scopes', s.key)} testId={`wa-scope-${s.key}-${user.id}`}
                disabled={s.key === 'money' && moneyLocked}
                label={s.key === 'money' && moneyLocked ? <span className="inline-flex items-center gap-1"><Lock size={10} />{s.label}</span> : s.label} />
            ))}
          </div>
          {moneyLocked && <div className="text-[10px] text-zinc-500 mt-1">Money hidden by the ERP price-visibility gate.</div>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Can do (confirm-gated)</div>
          <div className="flex flex-wrap gap-1.5">
            {catalog.write_scopes.map(s => (
              <Chip key={s.key} tone="rose" on={!!wa.scopes[s.key]} onClick={() => toggle('scopes', s.key)} label={s.label.split(' (')[0]} testId={`wa-scope-${s.key}-${user.id}`} />
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Push reports</div>
          <div className="flex flex-wrap gap-1.5">
            {catalog.notify.map(n => (
              <Chip key={n.key} on={!!wa.notify[n.key]} onClick={() => toggle('notify', n.key)} label={n.label.split(' ·')[0]} testId={`wa-notify-${n.key}-${user.id}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
